import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { activityStateEnum, activityTypeEnum, permissionLevelEnum, reportStateEnum, reportTypeEnum, stepModeEnum } from '../../../../constants';
import { useCreateActivityMutation } from '../../../../hooks/mutations/useActivityMutation';
import { useActivitiesTypesQuery } from '../../../../hooks/queries/useActivitiesTypesQuery';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { isSameDayOrAfter } from '../../../../utils/dates';
import CustomTimePicker from '../../../ui/CustomTimePicker';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface CreateActivityNeedsModalProps extends ModalProps {
    initDate: string;
    refetch?: () => void;
    initUserId?: string;
    initSiteId?: string;
    initActivityTypeId?: string;
    currentDate: Date;
    stepMode?: number;
    reportState?: number;
    activityState?: number;
    showSpecialUsers?: boolean;
    activities?: DataItem[];
}

const CreateActivityNeedsModal: React.FC<CreateActivityNeedsModalProps> = ({
    open,
    handleClose,
    initDate,
    refetch = () => {},
    initUserId = '',
    initSiteId = '',
    initActivityTypeId = '',
    currentDate,
    stepMode = stepModeEnum.PLANNING,
    reportState = reportStateEnum.PLANNING,
    activityState = activityStateEnum.PLANNING,
    showSpecialUsers = true,
    activities,
}) => {
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedActivityType, setSelectedActivityType] = useState<OptionType | null>(null);
    const [selectedSite, setSelectedSite] = useState<OptionType | null>(null);
    const [selectedDate, setSelectedDate] = useState(initDate);
    const { t } = useTranslation();
    const { user } = useAuth();

    const moTruckId = '66b097d4b0a672302822c715';
    const moAmplirollId = '66e14538c7639ac65e2e6763';

    const { data: dataSites, isLoading: isSitesLoading } = useSitesQuery({
        queries: { active: true, conducId: user?._id, assistantConducId: user?._id },
    });
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { data: dataActivitiesTypes, isLoading: isActivitiesTypesLoading } = useActivitiesTypesQuery({ queries: { active: true } });
    const {
        data: dataReports,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const { mutate: createActivity } = useCreateActivityMutation({
        onSuccess: (data) => {
            refetch();
            setSelectedUser(null);
            setSelectedActivityType(null);
            handleClose();
        },
    });

    const handleCreateActivity = () => {
        if (!selectedUser || !selectedSite || !selectedActivityType) return;
        createActivity({
            activityTypeId: selectedActivityType.value,
            siteId: selectedSite.value,
            startTime: selectedDate,
            userId: selectedUser.value,
            activityState,
            reportState,
            needsValidationTime: new Date().toISOString(),
            stepMode: stepMode,
        });
    };

    const checkSiteState = (siteId: string): number => {
        let tmpState = 0;
        const tmpActivities = activities?.filter((value) => value.siteId._id === siteId);
        if (!tmpActivities || tmpActivities.length === 0) return activityStateEnum.NEEDS;
        for (let i = 0; i < tmpActivities.length; i++) {
            if (tmpActivities[i].activityState > tmpState) tmpState = tmpActivities[i].activityState;
        }
        return tmpState;
    };

    const handleChangePerson = (value: OptionType | null) => {
        if (!value) {
            setSelectedUser(null);
            setSelectedActivityType(null);
            return;
        }
        setSelectedUser(value);
        const tmpUser = dataUsers?.find((user) => user._id === value.value);
        if (tmpUser) {
            if (tmpUser.profileId.permsLevel === permissionLevelEnum.CHAUFFEUR) {
                const activityType = dataActivitiesTypes?.find((value) => value.type === activityTypeEnum.TRUCK);
                if (activityType)
                    setSelectedActivityType({
                        label: activityType.designation,
                        value: activityType._id,
                    });
            } else setSelectedActivityType(null);
        }
    };

    useEffect(() => {
        if (initUserId.length) {
            const tmpUser = dataUsers?.find((user) => user._id === initUserId);
            if (tmpUser)
                setSelectedUser({
                    label: tmpUser ? formatName(tmpUser.firstName, tmpUser.lastName) : '',
                    value: initUserId,
                });
        }
    }, [dataUsers]);

    useEffect(() => {
        if (initSiteId.length) {
            const tmpSite = dataSites?.find((site) => site._id === initSiteId);
            if (tmpSite)
                setSelectedSite({
                    label: tmpSite ? tmpSite.name : '',
                    value: initSiteId,
                });
        }
    }, [dataSites]);

    useEffect(() => {
        if (initActivityTypeId.length) {
            const tmpActivityType = dataActivitiesTypes?.find((activityType) => activityType._id === initActivityTypeId);
            if (tmpActivityType)
                setSelectedActivityType({
                    label: tmpActivityType ? tmpActivityType.designation : '',
                    value: initActivityTypeId,
                });
        }
    }, [dataActivitiesTypes]);

    return (
        <ModalBody
            title={t('activity')}
            subtitle={t('add')}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                handleCreateActivity();
            }}
            disableSubmit={!selectedUser?.value.length || !selectedSite?.value.length || !selectedActivityType?.value.length}
        >
            <DropDown
                selectedItem={selectedSite}
                items={
                    dataSites
                        ?.filter((site) => checkSiteState(site._id) >= activityStateEnum.NEEDSFILLED)
                        ?.map((site) => ({ label: site.name, value: site._id })) || []
                }
                handleChange={(event, value) => setSelectedSite(value)}
                inputLabel={t('site')}
                loading={isSitesLoading}
                required
            />
            <DropDown
                selectedItem={selectedUser}
                items={
                    dataUsers
                        ?.filter(
                            (user) =>
                                dataReports?.find((report) => report.userId._id === user._id)?.reportType !== reportTypeEnum.ABSENT &&
                                user.profileId.permsLevel >= permissionLevelEnum.TERRAIN &&
                                (!user.arrivalDate || (user.arrivalDate && isSameDayOrAfter(currentDate, new Date(user.arrivalDate)))) &&
                                (showSpecialUsers || !user.isSpecial)
                        )
                        ?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                }
                handleChange={(event, value) => handleChangePerson(value)}
                inputLabel={t('person')}
                loading={isUsersLoading}
                required
            />
            <DropDown
                selectedItem={selectedActivityType}
                items={
                    dataActivitiesTypes
                        ?.filter((activityType) =>
                            [moAmplirollId, moTruckId].includes(selectedUser?.value || '')
                                ? activityType.type === activityTypeEnum.TRUCK
                                : activityType.type !== activityTypeEnum.TRUCK
                        )
                        .map((activityType) => ({ label: activityType.designation, value: activityType._id })) || []
                }
                handleChange={(event, value) => setSelectedActivityType(value)}
                inputLabel={t('activity')}
                loading={isActivitiesTypesLoading}
                required
            />
            <CustomTimePicker
                label={capitalizeFirstLetter(t('startTime'))}
                value={selectedDate}
                onChange={(isoString) => {
                    setSelectedDate(isoString);
                }}
                required={true}
            />
        </ModalBody>
    );
};

export default CreateActivityNeedsModal;
